<script lang="ts">
export type FilterOption = {
  label: string
  value: string
  icon?: string
}
</script>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    label: string
    options: FilterOption[]
    included: string[]
    excluded: string[]
    highlighted?: Set<string>
    size?: 'xs' | 'sm'
  }>(),
  { size: 'sm' },
)

const emit = defineEmits<{
  toggle: [value: string]
  clear: []
}>()

function getColor(value: string) {
  if (props.excluded.includes(value)) return 'error'
  if (props.included.includes(value)) return 'primary'
  if (props.highlighted?.has(value)) return 'primary'
  return 'neutral'
}

function getVariant(value: string) {
  if (props.excluded.includes(value) || props.included.includes(value)) return 'soft'
  return 'outline'
}

const hasFilters = computed(() => props.included.length > 0 || props.excluded.length > 0)
</script>

<template>
  <div class="flex items-start gap-2">
    <span class="text-sm text-muted w-36 shrink-0 pt-1.5">{{ label }}</span>
    <div class="flex gap-1 flex-wrap">
      <UButton
        v-for="option in options"
        :key="option.value"
        :color="getColor(option.value)"
        :variant="getVariant(option.value)"
        :size="size"
        @click="emit('toggle', option.value)"
      >
        <span v-if="excluded.includes(option.value)" class="mr-1">✕</span>
        <span v-if="included.includes(option.value)" class="mr-1">✓</span>
        <template v-if="option.icon">
          <UIcon v-if="option.icon.startsWith('i-')" :name="option.icon" class="mr-1" />
          <span v-else class="mr-1">{{ option.icon }}</span>
        </template>
        {{ option.label }}
        <slot name="option-action" :option="option" />
      </UButton>
      <UButton v-if="hasFilters" color="neutral" variant="ghost" size="xs" @click="emit('clear')">
        Clear
      </UButton>
    </div>
  </div>
</template>
